'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconClose24 } from '@cineamo/ui/src/icons/ic-close';

import { useAnalytics } from '../providers/analyticsProvider';

import { PREVIEWS_SELECTED_DATE } from './previewsUtils';

export type ShowtimesFinderTimeFilter = 'lt-18' | 'gt-18-lt-22' | 'gt-22';
export type ShowtimesFinderDimensionFilter = '2d' | '3d';
export type ShowtimesFinderLanguageFilter = 'ov' | 'omu';
export type ShowtimesFinderAccessibilityFilter = 'greta' | 'wheelchair';
export type ShowtimesFinderTechnologyFilter =
    | 'imax'
    | 'dolby-atmos'
    | 'dolby-vision'
    | 'dolby-cinema'
    | '4dx'
    | 'screenx'
    | 'hfr'
    | 'live'
    | 'dbox';

export type ShowtimesFinderFilters = {
    times: ShowtimesFinderTimeFilter[];
    dimensions: ShowtimesFinderDimensionFilter[];
    languages: ShowtimesFinderLanguageFilter[];
    technology: ShowtimesFinderTechnologyFilter[];
};

export const EMPTY_SHOWTIMES_FINDER_FILTERS: ShowtimesFinderFilters = {
    times: [],
    dimensions: [],
    languages: [],
    technology: []
};

const TIME_OPTIONS: ShowtimesFinderTimeFilter[] = ['lt-18', 'gt-18-lt-22', 'gt-22'];
const DIMENSION_OPTIONS: ShowtimesFinderDimensionFilter[] = ['2d', '3d'];
const LANGUAGE_OPTIONS: ShowtimesFinderLanguageFilter[] = ['ov', 'omu'];
const TECHNOLOGY_OPTIONS: ShowtimesFinderTechnologyFilter[] = [
    'imax',
    'dolby-atmos',
    'dolby-vision',
    'dolby-cinema',
    '4dx',
    'screenx',
    'hfr',
    'live',
    'dbox'
];
type FilterChipProps = {
    label: string;
    isSelected: boolean;
    onToggle: () => void;
};

function FilterChip({ label, isSelected, onToggle }: FilterChipProps) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className={cn(
                'px-12 py-4 rounded-4 text-body-bold-12 whitespace-nowrap transition-colors',
                isSelected
                    ? 'bg-[var(--primary-color)] text-[var(--on-primary-color)]'
                    : 'bg-[var(--search-background-color)] text-[var(--search-on-background-color)] hover:opacity-80'
            )}>
            {label}
        </button>
    );
}

type FilterSectionProps = {
    title: string;
    children: React.ReactNode;
};

function FilterSection({ title, children }: FilterSectionProps) {
    return (
        <div className="flex flex-col space-y-12">
            <span className="text-body-bold-14">{title}</span>
            <div className="flex flex-row flex-wrap gap-8">{children}</div>
        </div>
    );
}

type ShowtimesFinderFiltersPanelProps = {
    filters: ShowtimesFinderFilters;
    setFilters: (filters: ShowtimesFinderFilters) => void;
    onClose: () => void;
    showHeader?: boolean;
    selectedDate?: string;
};

export default function ShowtimesFinderFiltersPanel({
    filters,
    setFilters,
    onClose,
    showHeader = true,
    selectedDate
}: ShowtimesFinderFiltersPanelProps) {
    const t = useTranslations();
    const { track } = useAnalytics();

    // The time-of-day filter requires a single day; hide it for the multi-day views (ALL, PREVIEWS).
    const isTimeFilterVisible =
        selectedDate !== undefined && selectedDate !== 'all' && selectedDate !== PREVIEWS_SELECTED_DATE;

    // Every group is single-select: picking an option replaces the group's selection,
    // clicking the selected option again clears it.
    const toggle = <K extends keyof ShowtimesFinderFilters>(key: K, value: ShowtimesFinderFilters[K][number]) => {
        const current = filters[key] as Array<typeof value>;
        const next = current.includes(value) ? [] : [value];
        setFilters({ ...filters, [key]: next });
        // There is no Apply button — a filter takes effect the moment it is picked, so the pick
        // is the application.
        track('Showtimes Filters Applied', { filter: key, value: `${value}`, isCleared: next.length === 0 });
    };

    return (
        <div className="flex flex-col h-full bg-[var(--widget-background-color)] lg:rounded-12 overflow-hidden">
            {showHeader && (
                <div className="flex flex-row items-center justify-between px-12 pt-12 pb-12 flex-shrink-0">
                    <span className="text-headline-20">{t('showtimes-finder.filter.title')}</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-4 opacity-50 hover:opacity-100 transition-opacity"
                        aria-label={t('common.close')}>
                        <IconClose24 />
                    </button>
                </div>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto px-12 pb-16 space-y-16">
                {isTimeFilterVisible && (
                    <FilterSection title={t('showtimes-finder.filter.time-filter.title')}>
                        {TIME_OPTIONS.map((opt) => (
                            <FilterChip
                                key={opt}
                                label={t(`showtimes-finder.filter.time-filter.options.${opt}`)}
                                isSelected={filters.times.includes(opt)}
                                onToggle={() => toggle('times', opt)}
                            />
                        ))}
                    </FilterSection>
                )}

                <div className="flex flex-row gap-24 flex-wrap">
                    <FilterSection title={t('showtimes-finder.filter.dimension.title')}>
                        {DIMENSION_OPTIONS.map((opt) => (
                            <FilterChip
                                key={opt}
                                label={t(`showtimes-finder.filter.dimension.options.${opt}`)}
                                isSelected={filters.dimensions.includes(opt)}
                                onToggle={() => toggle('dimensions', opt)}
                            />
                        ))}
                    </FilterSection>
                    <FilterSection title={t('showtimes-finder.filter.language.title')}>
                        {LANGUAGE_OPTIONS.map((opt) => (
                            <FilterChip
                                key={opt}
                                label={t(`showtimes-finder.filter.language.options.${opt}`)}
                                isSelected={filters.languages.includes(opt)}
                                onToggle={() => toggle('languages', opt)}
                            />
                        ))}
                    </FilterSection>
                </div>

                <FilterSection title={t('showtimes-finder.filter.technology.title')}>
                    {TECHNOLOGY_OPTIONS.map((opt) => (
                        <FilterChip
                            key={opt}
                            label={t(`showtimes-finder.filter.technology.options.${opt}`)}
                            isSelected={filters.technology.includes(opt)}
                            onToggle={() => toggle('technology', opt)}
                        />
                    ))}
                </FilterSection>
            </div>
        </div>
    );
}
